<template>
  <div class="auth">
    <div class="login-form">
      <div class="form-left">
        <div class="language">
          <ui-select-language
            menuContentClass="language-select-menu"
            :applyImmediately="true"
          />
        </div>
        <div class="mb-10 mt-30">
          <img alt="BeMob" src="/images/logo-white2.svg" />
        </div>
        <p class="mb-32 pr-30">
          {{
            t(
              'BeMob helps to manage your ad campaigns, identify the most profitable traffic sources, measure your landing pages performance and provide highly detailed real-time reports.',
            )
          }}
        </p>
        <div class="login-signup">
          <div>{{ t("Don't have an account?") }}</div>
          <ui-button
            class="mt-4"
            :to="{ path: 'register' }"
            :text="t('Sign up')"
            icon="fas fa-user-plus"
          />
        </div>
        <div class="copyright">
          2015-{{ moment().year() }}
          {{ t('© BeMob. All rights reserved.') }}
        </div>
      </div>

      <div v-if="!fromEmail && step === 1" class="form-right">
        <div class="form-right-inner">
          <a @click="redirectToLogin" class="back-link d-flex align-center">
            <ui-icon
              :color="$colors.blue"
              class="mr-8"
              size="12"
              icon="fas fa-arrow-left"
            />
            {{ t('Back') }}
          </a>
          <div class="login-title">{{ t('Password Reset') }}</div>
          <ui-input
            class="ui-input__login mb-16"
            autofocus
            @keyup.enter="submitStep1"
            append-inner-icon="fas fa-envelope"
            :label="t('Email') + '*'"
            :placeholder="t('Email')"
            type="email"
            v-model="resetForm.email"
            name="email"
            :error-messages="fieldErrors['email']"
          />
          <div class="login-recaptcha">
            <RecaptchaV2
              @expired-callback="onRecaptchaExpired"
              @load-callback="onRecaptchaVerify"
            />
          </div>
          <ui-button
            class="w100"
            button-style="contained"
            :loading="apiLoading"
            @click="submitStep1"
            :disabled="!resetForm.email || !resetForm.recaptchaResponse"
            :text="t('Send password reset link')"
          >
            <template #icon-append>
              <ui-icon
                class="ml-10"
                :color="$colors.white"
                size="12"
                icon="fas fa-arrow-right"
              />
            </template>
          </ui-button>
        </div>
      </div>

      <div v-if="!fromEmail && step === 2" class="form-right">
        <div class="form-right-inner">
          <div class="login-title">{{ t('Password Reset') }}</div>
          <a @click="redirectToLogin" class="back-link d-flex align-center">
            <ui-icon
              :color="$colors.blue"
              class="mr-8"
              size="12"
              icon="fas fa-arrow-left"
            />
            {{ t('Back') }}
          </a>
          <div class="confirm-pass-text mb-16">
            {{
              t(
                'The link for password resetting was sent to your email. Please check the inbox (or spam folder) and click on Change Password button.',
              )
            }}
          </div>
          <ui-button
            class="w100"
            button-style="contained"
            :loading="apiPasswordResetLoading"
            @click="redirectToLogin"
            :text="t('Okay')"
          >
            <template #icon-append>
              <ui-icon
                class="ml-10"
                :color="$colors.white"
                size="12"
                icon="fas fa-check"
              />
            </template>
          </ui-button>
        </div>
      </div>

      <div v-if="fromEmail" class="form-right new-password">
        <div class="form-right-inner">
          <div class="login-title">{{ t('Password Reset') }}</div>
          <router-link :to="{ path: 'login' }" class="back-link d-flex align-center">
            <ui-icon
              :color="$colors.blue"
              class="mr-8"
              size="12"
              icon="fas fa-arrow-left"
            />
            {{ t('Back') }}
          </router-link>
          <div class="error-block mb-22 d-flex align-center justify-start" v-if="error">
            <div class="error-text">
              <ui-icon
                size="12"
                class="mr-8"
                :color="$colors.graphite"
                icon="fas fa-exclamation-triangle"
              />
              {{ errorMessage }}
            </div>
          </div>
          <ui-input
            class="ui-input__login mb-16"
            autofocus
            type="password"
            @keyup.enter="submitStep2"
            append-inner-icon="fas fa-lock"
            :label="t('Password') + '*'"
            :placeholder="t('Password')"
            v-model="newPasswordForm.password"
            name="password"
            :error-messages="fieldErrors['password']"
          />
          <ui-button
            v-if="!tokenExpired"
            class="w100"
            button-style="contained"
            :loading="apiLoading"
            :disabled="!newPasswordForm.password"
            @click="submitStep2"
            :text="t('Set new password')"
          >
            <template #icon-append>
              <ui-icon
                class="ml-10"
                size="12"
                :color="$colors.white"
                icon="fas fa-arrow-right"
              />
            </template>
          </ui-button>
          <ui-button
            v-else
            class="w100"
            button-style="contained"
            :loading="apiLoading"
            @click="redirectToForgotPassword"
            :text="t('Request another')"
          >
            <template #icon-append>
              <ui-icon
                class="ml-10"
                size="12"
                :color="$colors.white"
                icon="fas fa-arrow-right"
              />
            </template>
          </ui-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RecaptchaV2 } from 'vue3-recaptcha-v2';

import { reactive, computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment-timezone';
import { useColors, useI18n } from '@/composables';
import {
  forgotPasswordApiRequest,
  setNewPasswordApiRequest,
} from '@/services/api/auth/auth-forgot-password-api';
import type {
  ForgotPasswordForm,
  SetNewPasswordForm,
} from '@/services/api/auth/auth-forgot-password-api';
import { setAuthToken, removeAuthToken } from '@/services/api/main/axios';
import parseApiErrors from '@/utils/parseApiErrors';
import type { FieldErrors } from '@/services/models';

const { t } = useI18n();
const $colors = useColors();
const route = useRoute();
const router = useRouter();

const userId = computed(() => route.query.accountId as string | undefined);

const redirectToLogin = () => {
  router.push(
    userId.value
      ? { path: '/login/member', query: { accountId: userId.value } }
      : '/login',
  );
};

const redirectToForgotPassword = () => {
  step.value = 1;
  clearStep2Banner();
  tokenExpired.value = false;
  router.push('/forgot-password');
};

const fromEmail = computed(() => !!(route.query.code && route.query.uid));
const step = ref(1);

const resetForm = reactive({
  email: '',
  recaptchaResponse: '',
});

const newPasswordForm = reactive({
  password: '',
});

const apiLoading = ref(false);
const apiPasswordResetLoading = ref(false);
const fieldErrors = ref<FieldErrors>({});
const tokenExpired = ref(false);
const error = ref(false);
const errorMessage = ref('');

const clearStep2Banner = () => {
  error.value = false;
  errorMessage.value = '';
};

const apiSubmitReset = async (form: ForgotPasswordForm) => {
  apiLoading.value = true;
  fieldErrors.value = {};

  try {
    await forgotPasswordApiRequest({ ...form, userId: userId.value });
    step.value = 2;
  } catch (errorUnknown: unknown) {
    fieldErrors.value = parseApiErrors({ error: errorUnknown });
  } finally {
    apiLoading.value = false;
  }
};

const apiSubmitPassword = async (form: SetNewPasswordForm) => {
  apiLoading.value = true;
  fieldErrors.value = {};
  clearStep2Banner();

  try {
    setAuthToken(route.query.code as string);
    await setNewPasswordApiRequest(form);
    removeAuthToken();
    redirectToLogin();
  } catch (errorUnknown: unknown) {
    const status = (errorUnknown as { response?: { status?: number } })?.response?.status;
    if (status === 400) {
      error.value = true;
      errorMessage.value = t('Wrong email');
    } else if (status === 401) {
      error.value = true;
      errorMessage.value = t('Your password recovery link is expired');
      tokenExpired.value = true;
    } else if (status === 403) {
      error.value = true;
      errorMessage.value = t('Your password recovery link is expired');
    } else if (status === 410) {
      error.value = true;
      errorMessage.value = t('Your password recovery link is expired');
      tokenExpired.value = true;
    }
    fieldErrors.value = parseApiErrors({ error: errorUnknown });
  } finally {
    apiLoading.value = false;
  }
};

const onRecaptchaVerify = (response: string) => {
  resetForm.recaptchaResponse = response;
};

const onRecaptchaExpired = () => {
  resetForm.recaptchaResponse = '';
};

const submitStep1 = () => {
  apiSubmitReset(resetForm);
};

const submitStep2 = () => {
  apiSubmitPassword(newPasswordForm);
};
</script>

<style lang="scss" scoped>
.confirm-pass-text {
  font-size: 13px;
  color: var(--color-graphite);
  line-height: 1.69;
  letter-spacing: normal;
  text-align: center;
  margin-top: -16px;
  padding: 0 20px;
}

.back-link {
  z-index: 10;
  position: absolute;
  left: 25px;
  top: 38px;
  color: var(--color-blue);
  text-decoration: none;
  cursor: pointer;
}

.new-password {
  :deep(input) {
    padding: 0 30px 0 0 !important;
  }
}
</style>
